import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ASINSalesHistoryRow, ASINSalesHistoryCompany, ASINSalesHistoryKPIs } from '@/types/asinSalesHistory';
import { asinSalesHistoryService } from '@/services/asinSalesHistoryService';

interface ASINSalesHistoryFilter {
  asin?: string;
  dateFrom?: Date;
  dateTo?: Date;
}

interface ASINSalesHistoryContextValue {
  companies: ASINSalesHistoryCompany[];
  selectedCompany: string | null;
  selectedFilter: ASINSalesHistoryFilter | null;
  availableASINs: string[];
  isLoadingCompanies: boolean;
  isLoadingAsins: boolean;
  setSelectedCompany: (company: string | null) => void;
  setSelectedFilter: (filter: ASINSalesHistoryFilter | null) => void;
  
  // Additional properties for data access
  data: ASINSalesHistoryRow[];
  kpis: ASINSalesHistoryKPIs | null;
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  isContextReady: boolean;
}

const ASINSalesHistoryContext = createContext<ASINSalesHistoryContextValue | undefined>(undefined);

export const useASINSalesHistory = () => {
  const context = useContext(ASINSalesHistoryContext);
  if (!context) {
    throw new Error('useASINSalesHistory must be used within ASINSalesHistoryProvider');
  }
  return context;
};

interface ASINSalesHistoryProviderProps {
  children: ReactNode;
}

export const ASINSalesHistoryProvider: React.FC<ASINSalesHistoryProviderProps> = ({ children }) => {
  const [companies, setCompanies] = useState<ASINSalesHistoryCompany[]>([]);
  const [selectedCompany, setSelectedCompanyState] = useState<string | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<ASINSalesHistoryFilter | null>(null);
  const [availableASINs, setAvailableASINs] = useState<string[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [isLoadingAsins, setIsLoadingAsins] = useState(false);
  const [data, setData] = useState<ASINSalesHistoryRow[]>([]);
  const [kpis, setKpis] = useState<ASINSalesHistoryKPIs | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isContextReady, setIsContextReady] = useState(false);

  // Load companies on mount (matching Days of Cover pattern)
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        console.log('🚀 [ASINSalesHistoryContext] Starting loadCompanies...');
        console.log('🚀 [ASINSalesHistoryContext] Service available:', !!asinSalesHistoryService);
        console.log('🚀 [ASINSalesHistoryContext] Current companies state:', companies.length);
        console.log('🚀 [ASINSalesHistoryContext] IsLoadingCompanies:', isLoadingCompanies);
        
        console.log('🚀 [ASINSalesHistoryContext] Calling asinSalesHistoryService.getCompanies()...');
        const companiesData = await asinSalesHistoryService.getCompanies();
        console.log('✅ [ASINSalesHistoryContext] Service returned companies:', companiesData.length, 'companies');
        console.log('✅ [ASINSalesHistoryContext] Companies data:', companiesData);
        
        setCompanies(companiesData);
        setIsLoadingCompanies(false);
        console.log('✅ [ASINSalesHistoryContext] State updated - companies set, loading = false');
      } catch (error) {
        console.error('❌ [ASINSalesHistoryContext] Error loading companies:', error);
        console.error('❌ [ASINSalesHistoryContext] Error type:', typeof error);
        if (error instanceof Error) {
          console.error('❌ [ASINSalesHistoryContext] Error message:', error.message);
          console.error('❌ [ASINSalesHistoryContext] Error stack:', error.stack);
        }
        setCompanies([]);
        setIsLoadingCompanies(false);
        setError(error instanceof Error ? error.message : 'Failed to load companies');
        console.log('❌ [ASINSalesHistoryContext] Error state set - companies = [], loading = false, error set');
      }
    };

    console.log('🎯 [ASINSalesHistoryContext] useEffect triggered for loadCompanies');
    loadCompanies();
  }, []);

  // Initialize context when companies are loaded
  useEffect(() => {
    if (companies.length > 0 && !isContextReady) {
      console.log('[ASINSalesHistory] Initializing context with', companies.length, 'companies');
      const persistedCompany = localStorage.getItem('asinSalesHistory_selectedCompany');
      const persistedFilter = localStorage.getItem('asinSalesHistory_selectedFilter');
      
      console.log('[ASINSalesHistory] Persisted company:', persistedCompany);
      console.log('[ASINSalesHistory] Persisted filter:', persistedFilter);
      
      if (persistedCompany) {
        const companyExists = companies.some(c => c.id === persistedCompany);
        console.log('[ASINSalesHistory] Company exists in list:', companyExists);
        
        if (companyExists) {
          console.log('[ASINSalesHistory] Restoring valid persisted company');
          setSelectedCompanyState(persistedCompany);
          if (persistedFilter) {
            try {
              setSelectedFilter(JSON.parse(persistedFilter));
            } catch (e) {
              console.warn('[ASINSalesHistory] Invalid persisted filter, clearing');
              localStorage.removeItem('asinSalesHistory_selectedFilter');
            }
          }
        } else {
          console.warn('[ASINSalesHistory] Persisted company not found in list, clearing:', persistedCompany);
          localStorage.removeItem('asinSalesHistory_selectedCompany');
          localStorage.removeItem('asinSalesHistory_selectedFilter');
        }
      }
      
      console.log('[ASINSalesHistory] Context initialization complete');
      setIsContextReady(true);
    }
  }, [companies, isContextReady]);

  // Persist selected company changes
  useEffect(() => {
    if (!isContextReady) return;
    
    if (selectedCompany) {
      localStorage.setItem('asinSalesHistory_selectedCompany', selectedCompany);
    } else {
      localStorage.removeItem('asinSalesHistory_selectedCompany');
    }
  }, [selectedCompany, isContextReady]);

  // Persist selected filter changes
  useEffect(() => {
    if (!isContextReady) return;
    
    if (selectedFilter) {
      localStorage.setItem('asinSalesHistory_selectedFilter', JSON.stringify(selectedFilter));
    } else {
      localStorage.removeItem('asinSalesHistory_selectedFilter');
    }
  }, [selectedFilter, isContextReady]);

  // Load data when company changes (using new service method)
  useEffect(() => {
    const loadData = async () => {
      console.log('[ASINSalesHistory] loadData effect triggered. isContextReady:', isContextReady, 'selectedCompany:', selectedCompany);
      
      if (!isContextReady) {
        console.log('[ASINSalesHistory] Context not ready, skipping loadData');
        return;
      }

      if (!selectedCompany) {
        console.log('[ASINSalesHistory] No company selected, clearing data');
        setAvailableASINs([]);
        setData([]);
        setKpis(null);
        return;
      }

      if (companies.length === 0) {
        console.log('[ASINSalesHistory] Companies not loaded yet, skipping loadData');
        return;
      }

      const company = companies.find(c => c.id === selectedCompany);
      if (!company) {
        console.warn('[ASINSalesHistory] Company not found in list, clearing selection:', selectedCompany);
        setSelectedCompanyState(null);
        setSelectedFilter(null);
        localStorage.removeItem('asinSalesHistory_selectedCompany');
        localStorage.removeItem('asinSalesHistory_selectedFilter');
        setAvailableASINs([]);
        setData([]);
        setKpis(null);
        return;
      }

      console.log('[ASINSalesHistory] Loading data for company:', company.client);
      setIsLoadingAsins(true);
      try {
        // Get sales data using the new service method
        const salesResponse = await asinSalesHistoryService.getASINSalesHistoryData(selectedCompany);
        
        console.log('[ASINSalesHistory] Data loaded for company:', salesResponse.rows.length, 'rows');
        
        // Extract unique ASINs from the rows data
        const uniqueASINs = Array.from(new Set(salesResponse.rows.map(row => row[1]).filter(Boolean))).sort();
        setAvailableASINs(uniqueASINs);
        
        // Convert rows to ASINSalesHistoryRow format
        let filteredRows = salesResponse.rows;
        
        // Apply filters if specified
        if (selectedFilter?.asin) {
          filteredRows = filteredRows.filter(row => row[1] === selectedFilter.asin);
        }
        
        if (selectedFilter?.dateFrom || selectedFilter?.dateTo) {
          filteredRows = filteredRows.filter(row => {
            const rowDate = new Date(row[0]);
            if (selectedFilter.dateFrom && rowDate < selectedFilter.dateFrom) return false;
            if (selectedFilter.dateTo && rowDate > selectedFilter.dateTo) return false;
            return true;
          });
        }
        
        const formattedData = filteredRows.map((row, index) => ({
          id: `${row[1]}_${row[0]}`,
          company: selectedCompany,
          asin: row[1] || '',
          date: new Date(row[0] || ''),
          traffic: parseFloat(row[2]) || 0,
          conversionRate: parseFloat(row[3]) || 0,
          units: parseInt(row[4]) || 0,
          revenue: parseFloat(row[5]) || 0,
          ppcSpend: parseFloat(row[6]) || 0,
          grossProfit: parseFloat(row[7]) || 0,
          syncedAt: new Date(),
          sourceRow: index + 2
        }));
        
        setData(formattedData);
        setKpis(salesResponse.kpis);
        setError(null);
        
      } catch (error) {
        console.error('[ASINSalesHistory] Error loading data:', error);
        setAvailableASINs([]);
        setData([]);
        setKpis(null);
        setError(error instanceof Error ? error.message : 'Failed to load data');
      } finally {
        setIsLoadingAsins(false);
      }
    };

    loadData();
  }, [selectedCompany, companies, isContextReady, selectedFilter]);

  const setSelectedCompany = (company: string | null) => {
    setSelectedCompanyState(company);
    setSelectedFilter(null);
  };

  const refreshData = async () => {
    if (selectedCompany) {
      const company = companies.find(c => c.id === selectedCompany);
      if (company) {
        setLoading(true);
        try {
          const salesResponse = await asinSalesHistoryService.getASINSalesHistoryData(selectedCompany);
          
          // Apply current filters
          let filteredRows = salesResponse.rows;
          
          if (selectedFilter?.asin) {
            filteredRows = filteredRows.filter(row => row[1] === selectedFilter.asin);
          }
          
          if (selectedFilter?.dateFrom || selectedFilter?.dateTo) {
            filteredRows = filteredRows.filter(row => {
              const rowDate = new Date(row[0]);
              if (selectedFilter.dateFrom && rowDate < selectedFilter.dateFrom) return false;
              if (selectedFilter.dateTo && rowDate > selectedFilter.dateTo) return false;
              return true;
            });
          }
          
          const formattedData = filteredRows.map((row, index) => ({
            id: `${row[1]}_${row[0]}`,
            company: selectedCompany,
            asin: row[1] || '',
            date: new Date(row[0] || ''),
            traffic: parseFloat(row[2]) || 0,
            conversionRate: parseFloat(row[3]) || 0,
            units: parseInt(row[4]) || 0,
            revenue: parseFloat(row[5]) || 0,
            ppcSpend: parseFloat(row[6]) || 0,
            grossProfit: parseFloat(row[7]) || 0,
            syncedAt: new Date(),
            sourceRow: index + 2
          }));
          
          setData(formattedData);
          setKpis(salesResponse.kpis);
          setError(null);
          
        } catch (error) {
          console.error('[ASINSalesHistory] Error refreshing data:', error);
          setError(error instanceof Error ? error.message : 'Failed to refresh data');
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const value: ASINSalesHistoryContextValue = {
    companies,
    selectedCompany,
    selectedFilter,
    availableASINs,
    isLoadingCompanies,
    isLoadingAsins,
    setSelectedCompany,
    setSelectedFilter,
    data,
    kpis,
    loading,
    error,
    refreshData,
    isContextReady
  };

  return (
    <ASINSalesHistoryContext.Provider value={value}>
      {children}
    </ASINSalesHistoryContext.Provider>
  );
};